import { motion } from "framer-motion";
import { User } from "lucide-react";
import SectionLabel from "./SectionLabel";

const founders = [
  {
    name: "Darshan",
    img: "https://customer-assets-rejwkqb3.emergentagent.net/job_edit-showcase-270/artifacts/1da2a0d3426ac49d_Screenshot%202026-10-13%20133115.png",
    bio: "Darshan leads sales and marketing at the studio. From onboarding new shows to shaping positioning and client communication, he makes sure every relationship starts strong. His focus is on packaging our work so founders and businesses see the value before the first call.",
    testId: "founder-darshan",
  },
  {
    name: "Vedanth",
    img: "/vedanth.jpg",
    bio: "Vedanth runs operations behind the scenes. He owns briefs, deadlines, quality checks and the delivery systems that keep every episode, trailer and clip shipping on time. His focus is on making the studio reliable and consistent as we scale.",
    testId: "founder-vedanth",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative px-6 md:px-16 lg:px-32 py-24 md:py-32 bg-[#070707] border-y border-[#1a1a1a]"
    >
      <div className="max-w-7xl mx-auto">
        <SectionLabel number="06" label="About Us" testId="about-label" />

        <h2 className="font-display font-bold uppercase text-2xl md:text-4xl tracking-tighter leading-[0.95] [word-spacing:0.18em] mb-14 max-w-3xl">
          The production partner<br />
          <span className="text-[#F4C75E]">businesses trust.</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {founders.map((f, i) => (
            <motion.article
              key={f.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              data-testid={f.testId}
              className="group relative bg-[#0A0A0A] border border-[#1f1f1f] overflow-hidden hover:border-[#F4C75E]/40 transition-colors duration-500"
            >
              {/* Image (full bleed to card edges) or placeholder */}
              <div className="relative w-full aspect-[4/3] bg-[#0A0A0A] overflow-hidden">
                {f.img ? (
                  <img
                    src={f.img}
                    alt={`${f.name}, Founder of Podcast Studio`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-[50%_30%]"
                  />
                ) : (
                  <div
                    aria-hidden
                    className="absolute inset-0 flex items-center justify-center"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, rgba(244,199,94,0.05) 0px, rgba(244,199,94,0.05) 1px, transparent 1px, transparent 16px)",
                    }}
                  >
                    <div className="flex flex-col items-center gap-3 text-[#555]">
                      <div className="w-14 h-14 rounded-full border border-[#2a2a2a] flex items-center justify-center">
                        <User className="w-5 h-5" />
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.3em]">Photo coming soon</div>
                    </div>
                  </div>
                )}

                {/* Frame ticks */}
                <span className="absolute top-3 left-3 w-3 h-3 border-t border-l border-[#F4C75E]" />
                <span className="absolute top-3 right-3 w-3 h-3 border-t border-r border-[#F4C75E]" />
                <span className="absolute bottom-3 left-3 w-3 h-3 border-b border-l border-[#F4C75E]" />
                <span className="absolute bottom-3 right-3 w-3 h-3 border-b border-r border-[#F4C75E]" />

                {/* Bottom gradient + name tag (LHS) */}
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black via-black/80 to-transparent pointer-events-none" />
                <div className="absolute left-5 bottom-5 flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#F4C75E] font-bold">Founder</span>
                  <span className="font-display text-2xl md:text-3xl font-bold tracking-tight uppercase text-white">
                    {f.name}
                  </span>
                </div>
              </div>

              {/* Bio */}
              <div className="p-7 md:p-9">
                <p className="text-sm md:text-base leading-relaxed text-[#A3A3A3]">
                  {f.bio}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
